import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  draftSaveHasWork,
  getStoreWebsiteReadinessV3,
  useOpenTemplateDraft,
  usePublishWebsiteV3,
  websiteRegionForSlot,
  websiteTemplatesAdminKeys,
  type DraftSaveQueue,
  type SupabaseClient,
  type WebsiteEditRegion,
  type WebsiteReadinessIssue,
  type WebsiteTemplateManifest,
} from '@dukanti/shared';
import { isStaleDraft, publishErrorKey } from '../lib/issue-messages';

export type PublishStage =
  | 'closed'
  | 'checking'
  | 'issues'
  | 'confirm'
  | 'publishing'
  | 'success'
  | 'error';

export interface PublishIssueRow {
  readonly issue: WebsiteReadinessIssue;
  /** The region of the slot the issue is about, for its name and "Go to item". */
  readonly region: WebsiteEditRegion | null;
}

export interface PublishFlow {
  readonly stage: PublishStage;
  /** What stops the publish: shown in the `issues` stage. */
  readonly issues: readonly PublishIssueRow[];
  /** What is worth fixing but publishes anyway: shown in the `confirm` stage. */
  readonly warnings: readonly PublishIssueRow[];
  /** Admin locale key of the message of a failed step. */
  readonly errorKey: string | null;
  /**
   * Saves the latest edits first (the queue is paused from here until the
   * flow ends), then checks the draft; opens the issue list when something
   * blocks, otherwise the question.
   */
  readonly open: () => Promise<void>;
  readonly close: () => void;
  /** The merchant said Publish. */
  readonly confirm: () => Promise<void>;
  /** After a failure: the step that failed again. */
  readonly retry: () => Promise<void>;
}

export interface UsePublishFlowArgs {
  readonly client: SupabaseClient;
  readonly storeId: string;
  /** Epic #228 P3: the template whose draft goes live. Other templates' drafts stay as they are. */
  readonly templateId: string;
  readonly manifest: WebsiteTemplateManifest;
  /**
   * Epic #228 P4: the template's auto-save queue. Its `base` after the flush
   * is the head that goes live (the CAS of the publish).
   */
  readonly queue: DraftSaveQueue;
  readonly newMutationId?: () => string;
}

/**
 * Publishing is one flow inside a dialog, never a page: check the draft,
 * name what blocks and where (with a way to go there), ask (listing what is
 * only worth fixing), publish, say it is done. Readiness is asked fresh every time; nothing is cached, because
 * a draft may have changed a moment ago.
 *
 * A retry after a failed publish reuses its `clientMutationId`: if the first
 * attempt reached the server the merchant gets its result, not a second
 * revision.
 *
 * Epic #228 P4 (design §5.2, PD9): publishing never races auto-save. `open`
 * pauses the queue (timers cancelled) and flushes it; a failed flush resumes
 * and says "Save your latest changes first". The publish CASes on the
 * queue's base. A `stale_draft` from the publish (another tab published
 * another template and archived this head) reopens this template's draft,
 * adopts its head and publishes once more when nothing is waiting to be
 * saved. Every terminal path resumes the queue.
 */
export function usePublishFlow({
  client,
  storeId,
  templateId,
  manifest,
  queue,
  newMutationId = () => crypto.randomUUID(),
}: UsePublishFlowArgs): PublishFlow {
  const queryClient = useQueryClient();
  const publishMutation = usePublishWebsiteV3(client, storeId);
  const openDraft = useOpenTemplateDraft(client, storeId);
  const [stage, setStage] = useState<PublishStage>('closed');
  const [issues, setIssues] = useState<readonly PublishIssueRow[]>([]);
  const [warnings, setWarnings] = useState<readonly PublishIssueRow[]>([]);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const failedStepRef = useRef<'flush' | 'check' | 'publish'>('check');
  const mutationRef = useRef<{ readonly id: string; readonly revision: string } | null>(null);

  const check = useCallback(async (): Promise<void> => {
    setStage('checking');
    setErrorKey(null);
    try {
      // The server already splits what blocks from what is only worth fixing.
      const report = await queryClient.fetchQuery({
        queryKey: websiteTemplatesAdminKeys.readinessV3(storeId, templateId),
        queryFn: () => getStoreWebsiteReadinessV3(client, storeId, templateId),
        staleTime: 0,
      });
      const toRow = (issue: WebsiteReadinessIssue): PublishIssueRow => ({
        issue,
        region: issue.slotId ? websiteRegionForSlot(manifest, issue.slotId) : null,
      });
      const blocking = report.blocking.map(toRow);
      setIssues(blocking);
      setWarnings(report.warnings.map(toRow));
      setStage(blocking.length === 0 ? 'confirm' : 'issues');
    } catch {
      failedStepRef.current = 'check';
      setErrorKey('admin.websiteTemplates.errors.generic');
      queue.resume();
      setStage('error');
    }
  }, [client, manifest, queue, queryClient, storeId, templateId]);

  // The screen can go away mid-flow (browser Back, a signed-out redirect).
  // A queue left paused would buffer every later edit and never send it.
  useEffect(
    () => () => {
      queue.resume();
    },
    [queue],
  );

  /**
   * Fix round 1: defensive only. The Publish button is disabled while the
   * queue saves, failed, is in conflict or keeps a rejection, and every way
   * out of a region dialog flushes at once, so the flush below normally finds
   * nothing to send. If it ever cannot save, the error stage says "Save your
   * latest changes first" (no dedicated stage).
   */
  const flushFailed = useCallback((): void => {
    failedStepRef.current = 'flush';
    setErrorKey('admin.websiteTemplates.publish.flushFirst');
    queue.resume();
    setStage('error');
  }, [queue]);

  /** Pause, save what is waiting (normally nothing), then check. */
  const flushThenCheck = useCallback(async (): Promise<void> => {
    queue.pause();
    setStage('checking');
    setErrorKey(null);
    const flushed = await queue.flush();
    // A kept server-only rejection is not on the server: publishing would drop it.
    if (!flushed.ok || Object.keys(queue.getSnapshot().rejected).length > 0) {
      flushFailed();
      return;
    }
    await check();
  }, [check, flushFailed, queue]);

  /** PD9: reopen this template's draft and adopt its head. True when a retry may publish. */
  const recoverHead = useCallback(async (): Promise<boolean> => {
    try {
      const state = await openDraft.mutateAsync({ templateId, clientMutationId: newMutationId() });
      if (state.head === null || state.draft === null) return false;
      queue.adoptHead({ revisionId: state.head.revisionId, slots: state.draft.document.slots });
      return queue.getSnapshot().base === state.head.revisionId;
    } catch {
      return false;
    }
  }, [newMutationId, openDraft, queue, templateId]);

  const publishOnce = useCallback(
    async (mayRecover: boolean): Promise<void> => {
      const base = queue.getSnapshot().base;
      if (mutationRef.current?.revision !== base) {
        mutationRef.current = { id: newMutationId(), revision: base };
      }
      setStage('publishing');
      setErrorKey(null);
      try {
        await publishMutation.mutateAsync({
          templateId,
          expectedHeadRevisionId: base,
          clientMutationId: mutationRef.current.id,
        });
        mutationRef.current = null;
        queue.resume();
        setStage('success');
      } catch (error) {
        if (mayRecover && isStaleDraft(error)) {
          const adopted = await recoverHead();
          if (adopted && !draftSaveHasWork(queue.getSnapshot())) {
            await publishOnce(false);
            return;
          }
          if (draftSaveHasWork(queue.getSnapshot())) {
            flushFailed();
            return;
          }
        }
        failedStepRef.current = 'publish';
        setErrorKey(publishErrorKey(error));
        queue.resume();
        setStage('error');
      }
    },
    [flushFailed, newMutationId, publishMutation, queue, recoverHead, templateId],
  );

  const confirm = useCallback(async (): Promise<void> => {
    queue.pause();
    // Never publish over edits the server has not confirmed.
    if (draftSaveHasWork(queue.getSnapshot())) {
      await flushThenCheck();
      return;
    }
    await publishOnce(true);
  }, [flushThenCheck, publishOnce, queue]);

  const close = useCallback((): void => {
    queue.resume();
    setStage('closed');
    setIssues([]);
    setWarnings([]);
    setErrorKey(null);
  }, [queue]);

  const retry = useCallback(
    (): Promise<void> => (failedStepRef.current === 'publish' ? confirm() : flushThenCheck()),
    [confirm, flushThenCheck],
  );

  return { stage, issues, warnings, errorKey, open: flushThenCheck, close, confirm, retry };
}
